import React, { useState, useEffect, startTransition } from 'react';
import GlossaryCard from 'components/GlossaryCard';
import FilterBar from 'components/FilterBar';
import Pagination from 'components/Pagination';
import axios from 'axios';
import { parseDoubleEscapedJson } from 'utils/fixDoubleEscapedJSON';
import { useNavigate, useLocation } from 'react-router-dom';

const ITEMS_PER_PAGE = 10;
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#';

export default function AtoZView(longidObj) {

  const longid = longidObj.longid;

  const navigate = useNavigate();
  const location = useLocation();

  // STATE
  const [selectedLetter, setSelectedLetter] = useState(null);  // <-- starts with no selection
  const [search, setSearch] = useState('');
  const [masterData, setMasterData] = useState([]);
  const [searchHappened, setSearchHappened] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [riskFilter, setRiskFilter] = useState('all');
  const [sortBy, setSortBy] = useState('');
  const [tag, setTag] = useState('all');

  const [filtered, setFiltered] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

// check for/handle Glossary Links
    useEffect(() => {
    const searchTerms = location.search;
    if (!searchTerms.includes('?glossary=')) return;
    const term = decodeURIComponent(searchTerms.split('?glossary=')[1].split('/')[0] || '').trim();
    if (!term) return;
    // use AbortController to cancel the request if effect runs again / component unmounts
    const controller = new AbortController();
    navigate(`/search?glossary=${term}`, { replace: true });
  }, [location]);



  // FETCH
  const fetchData = async (char) => {
    if (!char) return;   // <-- prevents auto-fetch on initial load

    setSearchHappened(true);
    setLoading(true);
    setError(null);

    if (char !== '#') {
      try {
      const url = longid ? `/edgconnect/term/alpha/${selectedLetter}?longid=${longid}` : `/edgconnect/term/alpha/${selectedLetter}?longid=90fa602c`
      const res = await axios.get(url);
      const data = parseDoubleEscapedJson(res.data);
      const tempData = data.pageItems || [];
      setMasterData(tempData)
    }
    catch (err) {
      console.error(err);
      setError('Failed to load item details.');
    }
    finally {
      setLoading(false);
    }
  } else try {
    const url = longid ? `/edgconnect/term/alphaOther?longid=${longid}` : `/edgconnect/term/alphaOther?longid=90fa602c`
  const alphaRes = await axios.get(url);
  const alphaData = parseDoubleEscapedJson(alphaRes.data);
  const tempAlphaData = alphaData.pageItems || [];
  setMasterData(tempAlphaData)
  }
  catch (err) {
      console.error(err);
      setError('Failed to load item details.');
    }
    finally {
      setLoading(false);
    }
}

// Fetch on initial load
useEffect(() => {
  const params = new URLSearchParams(location.search);
  const alphaFromUrl = params.get('alpha');
  if (alphaFromUrl && alphaFromUrl.length === 1) {
    setSelectedLetter(alphaFromUrl.toUpperCase());
  }
}, []); // only run on mount


  // Fetch on interaction
  useEffect(() => {
    if (selectedLetter) {
      fetchData(selectedLetter);

    const params = new URLSearchParams(location.search);
    params.set('alpha', selectedLetter);
    navigate(`/a-z?${params.toString()}`, { replace: true });
  }
}, [selectedLetter]);

  // TAG OPTIONS
  const tagOptions = React.useMemo(() => {
    const setTags = new Set();
    masterData?.forEach(item => {
      (item.tags || []).forEach(t => setTags.add(t));
    });
    return Array.from(setTags).sort();
  }, [masterData]);

  // FILTERING + SORTING
  useEffect(() => {
    if (!selectedLetter) {
      setFiltered([]);
      return;
    }

    const matchesLetter = (item) => {
      const name = (item.name || '').trim().toUpperCase();
      if (!name) return false;

      if (selectedLetter === '#') {
        return !/^[A-Z]/.test(name[0]);
      }
      return name.startsWith(selectedLetter);
    };

    const matchesSearch = (item) =>
      !search || (item.name || '').toLowerCase().includes(search.toLowerCase());

    const matchesRisk = (item) => {
      if (riskFilter === 'all') return true;
      const s = item.sensitivity || [];
      return s.some(val => String(val).toLowerCase() === riskFilter.toLowerCase());
    };

    const matchesTag = (item) =>
      tag === 'all' || (item.tags || []).includes(tag);

    let output = masterData.filter(item =>
      matchesLetter(item) &&
      matchesSearch(item) &&
      matchesRisk(item) &&
      matchesTag(item)
    );

    // SORTING
    if (sortBy === 'alphabetical') {
      output.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
    } else if (sortBy === 'status') {
      output.sort((a, b) => (a.status || '').localeCompare(b.status || ''));
    } else if (sortBy === 'lastUpdated') {
      output.sort((a, b) => {
        const dateA = a.lastUpdated ? new Date(a.lastUpdated) : 0;
        const dateB = b.lastUpdated ? new Date(b.lastUpdated) : 0;
        return dateB - dateA;
      });
    }

    startTransition(() => {
      setFiltered(output);
      setCurrentPage(1);
    });

  }, [masterData, selectedLetter, search, riskFilter, tag, sortBy]);

  // PAGINATION
  const pageItems = React.useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, currentPage]);

  return (
    <div className="view">
      <div className="p-4 mb-4 border rounded-b-lg rounded-tr-lg shadow-md border-black/10 bg-grey-50">
        <div className="pb-2 text-muted">Browse an alphabetical listing of all terms</div>

        {/* LETTER NAV */}
        <div className="flex flex-wrap gap-1 mb-4 letter-nav">
          {ALPHABET.split('').map(letter => (
            <button
              className="block py-2 px-[.95rem] rounded-[2px] hover--bg-ubc-blue hover--text-white text-sm shadow"
              key={letter}
              onClick={() => setSelectedLetter(letter)}
              style={{
                  background: selectedLetter === letter ? '#0b2540' : undefined,
                  color: selectedLetter === letter ? '#fff' : undefined,
                }}
                aria-pressed={selectedLetter === letter}
              >
                {letter}
            </button>
          ))}
        </div>
        <div class="text-muted text-right">
          Currently viewing:{' '}
          <strong>{selectedLetter ? selectedLetter : 'None'}</strong>
        </div>
      </div>
      <div className="p-4 mb-4 border rounded-lg shadow-md border-black/10 bg-grey-50">
        {selectedLetter && filtered.length > 0 && (
          <FilterBar
            className=""
            search={search}
            onSearch={setSearch}
            riskFilter={riskFilter}
            onRiskFilter={setRiskFilter}
            sortBy={sortBy}
            onSortBy={setSortBy}
            tag={tag}
            onTag={setTag}
            tagOptions={tagOptions}
          /> )}
          {/* HIDE PAGINATION UNTIL AFTER USER SELECTS A LETTER */}
          {selectedLetter && (
          <Pagination
            total={filtered.length}
            perPage={ITEMS_PER_PAGE}
            current={currentPage}
            onChange={(p) => setCurrentPage(p)}
          />
          )}

          <div>
            {!selectedLetter ? (
              <div className="my-3 text-muted">
                Please select the first letter of the term to begin browsing.
              </div>
            ) : pageItems.length === 0 ? (
              <div className="my-3 text-muted">No matching terms.</div>
            ) : (
              pageItems.map(term => <GlossaryCard key={term.id} term={term} />)
            )}
          </div>
          <Pagination
        total={filtered.length}
        perPage={ITEMS_PER_PAGE}
        current={currentPage}
        onChange={(p) => setCurrentPage(p)}
        />
        <div className="my-3 text-sm text-muted">
        {filtered.length > 0 && searchHappened && (
          <div>
            Viewing{' '}
            {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
            {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of{' '}
            {filtered.length} results
          </div>
        )}
      </div>
    </div>
  </div>
  );
}
